/**
 * Var i tiden en uthyrning befinner sig — härlett ur avtalsdatumen, inte
 * läst ur ett lagrat fält.
 *
 * Systemet har två parallella statusfält: `status` (sätts av flödet) och
 * `rentalStatus` (sätts av ett nattjobb). Nattjobbet rör aldrig `status`, så
 * en uthyrning vars period gått ut har `rentalStatus: COMPLETED` och
 * `status: FINAL_APPROVED` för alltid. Tre vyer räknade "aktiv" på tre olika
 * sätt, och ingen tittade på slutdatumet. Qvillestaden kunde inte skapa en ny
 * uthyrning på en lägenhet vars förra uthyrning tagit slut nio dagar tidigare.
 *
 * Det här är samma härledning som owner-v3 gör i ownerFlow.ts, lyft till en
 * delad modul så att admin-sidan räknar likadant. Härledning ur datum är
 * alltid rätt i dag — oavsett om nattjobbet körts.
 */

import { isSameApartment, type ApartmentFields } from './apartmentKey';
import { toDateOnly } from './dates';

export type RentalPhase =
  | 'TERMINATING'    // uppsägning pågår
  | 'NOT_STARTED'    // godkänd, avtalet har inte börjat
  | 'ACTIVE'
  | 'EXPIRING_SOON'  // 30 dagar eller mindre kvar
  | 'ENDED';         // slutdatumet har passerat

/** Statusar där en ansökan är en faktisk uthyrning, inte ett ärende under handläggning. */
export const RENTAL_STATUSES = ['FINAL_APPROVED', 'ACTIVE'] as const;

export interface RentalLike {
  status?: string;
  contractStartDate?: unknown;
  contractEndDate?: unknown;
  fromDate?: unknown;
  toDate?: unknown;
  terminationRequest?: unknown;
}

const DAY = 86_400_000;

function dayIndex(dateOnly: string | null): number | null {
  if (!dateOnly) return null;
  const [y, m, d] = dateOnly.split('-').map(Number);
  return Math.floor(Date.UTC(y, m - 1, d) / DAY);
}

/**
 * Första värdet som faktiskt är ett datum. Manuellt inlagda uthyrningar sparar
 * avtalsdatumen som TOM STRÄNG, och `??` släpper bara igenom null/undefined —
 * den tomma strängen valdes då framför toDate, uthyrningen fick inget slutdatum
 * och lägenheten förblev "Uthyrd" för alltid (Qvillestaden, sept 2026).
 */
export function firstDateOnly(...values: unknown[]): string | null {
  for (const value of values) {
    const date = toDateOnly(value as Parameters<typeof toDateOnly>[0]);
    if (date) return date;
  }
  return null;
}

/**
 * Fas för en uthyrning. `null` om ansökan inte är en uthyrning (PENDING,
 * APPROVED utan hyresgäst, REJECTED …) — då är frågan meningslös.
 *
 * `today` är ÅÅÅÅ-MM-DD; default är dagens datum i svensk tid.
 */
export function deriveRentalPhase(rental: RentalLike, today?: string): RentalPhase | null {
  if (!rental.status || !RENTAL_STATUSES.includes(rental.status as (typeof RENTAL_STATUSES)[number])) {
    return null;
  }
  if (rental.terminationRequest) return 'TERMINATING';

  const todayIdx = dayIndex(toDateOnly(today ?? new Date()));
  const startIdx = dayIndex(firstDateOnly(rental.contractStartDate, rental.fromDate));
  const endIdx = dayIndex(firstDateOnly(rental.contractEndDate, rental.toDate));
  if (todayIdx === null) return 'ACTIVE';

  if (endIdx !== null && endIdx < todayIdx) return 'ENDED';
  if (startIdx !== null && startIdx > todayIdx) return 'NOT_STARTED';
  if (endIdx !== null && endIdx - todayIdx <= 30) return 'EXPIRING_SOON';
  return 'ACTIVE';
}

/**
 * Statusmärket i listan över godkända uthyrningar. Listan hade en egen, fjärde
 * datumlogik (`new Date(toDate)`) som gav "Invalid Date" för tidsstämplar — raden
 * blev då aldrig avslutad och visades som "Aktiv uthyrning". En uthyrning som
 * inte har börjat är KOMMANDE: med periodregeln kan den ligga bredvid en
 * pågående på samma lägenhet, och då får raderna inte se likadana ut.
 */
export function rentalBadge(
  rental: RentalLike,
  today?: string,
): 'ENDED' | 'EXPIRING_SOON' | 'UPCOMING' | 'ACTIVE' | null {
  const phase = deriveRentalPhase(rental, today);
  if (phase === null) return null;
  if (phase === 'ENDED' || phase === 'EXPIRING_SOON') return phase;
  if (phase === 'NOT_STARTED') return 'UPCOMING';
  return 'ACTIVE';
}

/** Sant för uthyrningar som fortfarande upptar lägenheten: allt utom avslutade. */
export function isRentalCurrent(rental: RentalLike, today?: string): boolean {
  const phase = deriveRentalPhase(rental, today);
  return phase !== null && phase !== 'ENDED';
}

/**
 * Är lägenheten upptagen av någon av uthyrningarna?
 *
 * Nyckeln är samma som överallt annars (`buildApartmentKey`) — inte den råa
 * `${address}-${apartmentNumber}` som AddRentalModal använde, vilket var en
 * fjärde variant av lägenhetsnyckeln.
 */
export function findCurrentRental<T extends RentalLike & ApartmentFields>(
  rentals: T[],
  apartment: ApartmentFields,
  today?: string,
): T | null {
  for (const rental of rentals) {
    if (isSameApartment(rental, apartment) && isRentalCurrent(rental, today)) {
      return rental;
    }
  }
  return null;
}

export interface PeriodLike {
  fromDate?: unknown;
  toDate?: unknown;
  contractStartDate?: unknown;
  contractEndDate?: unknown;
  status?: string;
  step?: unknown;
}

/** Steg 1: ansökan väntar på styrelsens första beslut — inga avtalsdatum är valda än. */
const STEP_ONE_STATUSES = ['PENDING', 'ADDITIONAL_INFO', 'APPROVED', 'REJECTED'];

/**
 * Perioden en ansökan eller uthyrning gör anspråk på lägenheten.
 *
 * Avtalsdatumen gäller före ansökans — men bara när de faktiskt är valda. En
 * ansökan i STEG 1 sparar formulärets standarddatum (i dag + ett år) i
 * avtalsfälten, oavsett vilken period ägaren sökt. Där gäller fromDate/toDate.
 */
export function rentalPeriod(rental: PeriodLike): { start: string | null; end: string | null } {
  const stepOne = rental.step === 1 || (rental.step == null && STEP_ONE_STATUSES.includes(rental.status ?? ''));
  if (stepOne) {
    return { start: firstDateOnly(rental.fromDate), end: firstDateOnly(rental.toDate) };
  }
  return {
    start: firstDateOnly(rental.contractStartDate, rental.fromDate),
    end: firstDateOnly(rental.contractEndDate, rental.toDate),
  };
}

/**
 * Krockar den nya perioden med någon uthyrning på samma lägenhet?
 *
 * Förr spärrades lägenheten så fort den var "upptagen just nu". Det hindrade
 * både Hyria-admin och ägaren från att lägga in en KOMMANDE uthyrning medan en
 * annan pågår (Annie: pågående till 2026-11-30, ny från 2026-12-01). Rätt regel
 * är att bara spärra när perioderna överlappar. Dagarna är inklusiva: en ny
 * period får börja dagen EFTER att den förra slutar. En uthyrning utan
 * slutdatum spärrar allt efter sin start.
 *
 * `statuses`: vilka ärenden som räknas. Standard är faktiska uthyrningar; för
 * ägarens nya ansökan skickas även statusarna under handläggning in.
 */
export function findOverlappingRental<T extends RentalLike & ApartmentFields>(
  rentals: T[],
  apartment: ApartmentFields,
  period: PeriodLike,
  options: { statuses?: readonly string[] } = {},
): T | null {
  const statuses = options.statuses ?? RENTAL_STATUSES;
  const next = rentalPeriod(period);
  if (!next.start || !next.end) return null; // ofullständig period → inget att avgöra än

  for (const rental of rentals || []) {
    if (!rental.status || !statuses.includes(rental.status)) continue;
    if (!isSameApartment(rental, apartment)) continue;
    const existing = rentalPeriod(rental);
    const startsBeforeNextEnds = !existing.start || existing.start <= next.end;
    const endsAfterNextStarts = !existing.end || existing.end >= next.start;
    if (startsBeforeNextEnds && endsAfterNextStarts) return rental;
  }
  return null;
}

/**
 * Statusar där en ansökan fortfarande gör anspråk på lägenheten: allt utom
 * avslag. Används när en ÄGARE skickar in en ny ansökan — då räknas även
 * ärenden under handläggning, så samma period inte kan sökas två gånger.
 */
export const OPEN_APPLICATION_STATUSES = [
  'PENDING',
  'ADDITIONAL_INFO',
  'APPROVED',
  'TENANT_INFO_SUBMITTED',
  'CREDIT_CHECK_COMPLETE',
  'AWAITING_OWNER_DECISION',
  'FINAL_APPROVED',
  'CONTRACT_SIGNED',
  'ACTIVE',
] as const;

/**
 * Hindrar någon befintlig ansökan eller uthyrning den nya ansökan?
 *
 * Förr spärrades ägaren av VARJE ansökan som inte var avslagen, oavsett period.
 * En ägare vars uthyrning tagit slut kunde aldrig ansöka igen, och Annie kunde
 * inte söka för perioden efter sin pågående uthyrning. Nu krävs att perioderna
 * överlappar.
 */
export function findBlockingApplication<T extends RentalLike & ApartmentFields>(
  existing: T[],
  next: ApartmentFields & PeriodLike,
): T | null {
  return findOverlappingRental(existing, next, next, { statuses: OPEN_APPLICATION_STATUSES });
}
